import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '~/api/client';
import type { ApplicationCard, ApplicationType, CatalogFilters, CatalogSort, PricingModel } from '~/api/types';
import { ApplicationGrid } from '~/features/marketplace/ApplicationCard';
import {
  Button,
  EmptyState,
  ErrorPanel,
  PageHeading,
  Select,
  SkeletonGrid,
  Switch,
  SwitchRow,
  classes,
} from '~/shared/ui';
import { useQuery } from '~/shared/hooks/useQuery';

/**
 * Search and filtering (§17, §18).
 *
 * Filter state lives in the URL rather than in component state. That is what makes a filtered view
 * shareable and bookmarkable, survives a reload, and — because the query string is the cache key on
 * the API side — makes two users who chose the same filters share a cached response.
 */

const TYPE_OPTIONS: readonly { value: ApplicationType; label: string }[] = [
  { value: 'WORKER', label: 'Worker' },
  { value: 'WEB_APP', label: 'Web app' },
  { value: 'API', label: 'API' },
  { value: 'INTEGRATION', label: 'Integration' },
  { value: 'PLUGIN', label: 'Plugin' },
  { value: 'SAAS', label: 'SaaS' },
  { value: 'OTHER', label: 'Other' },
];

const SORT_OPTIONS: readonly { value: CatalogSort; label: string }[] = [
  { value: 'relevance', label: 'Best match' },
  { value: 'popular', label: 'Most installed' },
  { value: 'rating', label: 'Highest rated' },
  { value: 'newest', label: 'Newest' },
  { value: 'name', label: 'Name' },
];

export function BrowsePage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const [extraPages, setExtraPages] = useState<readonly ApplicationCard[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const filters = useMemo<CatalogFilters>(
    () => ({
      q: params.get('q') ?? undefined,
      category: params.get('category') ?? undefined,
      tag: params.getAll('tag'),
      type: params.getAll('type').filter(isApplicationType),
      pricing: params.getAll('pricing').filter(isPricingModel),
      developer: params.get('developer') ?? undefined,
      minRating: readNumber(params.get('minRating')),
      verifiedOnly: params.get('verifiedOnly') === 'true',
      safeOnly: params.get('safeOnly') === 'true',
      sort: readSort(params.get('sort'), params.get('q')),
    }),
    [params],
  );

  // The filter signature, not the object, is the dependency: a new object with the same contents
  // must not refetch.
  const signature = params.toString();
  const firstPage = useQuery((signal) => api.browse(filters, {}, signal), [signature]);

  /*
   * "Load more" appends rather than replacing, and resetting is tied to the filter signature so
   * changing a filter cannot leave the previous filter's results appended below the new ones.
   */
  const applications = useMemo(
    () => [...(firstPage.data?.items ?? []), ...extraPages],
    [firstPage.data, extraPages],
  );
  const nextCursor = cursor ?? firstPage.data?.nextCursor ?? undefined;

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(params);
      mutate(next);
      setExtraPages([]);
      setCursor(undefined);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const loadMore = useCallback(async () => {
    if (nextCursor === undefined) return;
    setIsLoadingMore(true);
    try {
      const page = await api.browse(filters, { cursor: nextCursor });
      setExtraPages((current) => [...current, ...page.items]);
      setCursor(page.nextCursor ?? undefined);
    } finally {
      setIsLoadingMore(false);
    }
  }, [filters, nextCursor]);

  const activeFilterCount =
    (filters.category === undefined ? 0 : 1) +
    (filters.type?.length ?? 0) +
    (filters.pricing?.length ?? 0) +
    (filters.tag?.length ?? 0) +
    (filters.verifiedOnly === true ? 1 : 0) +
    (filters.safeOnly === true ? 1 : 0) +
    (filters.minRating === undefined ? 0 : 1);

  return (
    <>
      <PageHeading
        title={filters.q === undefined ? 'All applications' : `Results for “${filters.q}”`}
        subtitle={
          firstPage.data === undefined
            ? undefined
            : `${String(applications.length)}${
                nextCursor === undefined ? '' : '+'
              } applications${activeFilterCount > 0 ? ' matching your filters' : ''}`
        }
        action={
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <span className="hidden sm:inline">Sort</span>
            <Select
              aria-label="Sort applications"
              className="w-44"
              value={filters.sort ?? 'popular'}
              options={SORT_OPTIONS.filter(
                (option) => option.value !== 'relevance' || filters.q !== undefined,
              )}
              onChange={(value) => {
                update((next) => {
                  next.set('sort', value);
                });
              }}
            />
          </div>
        }
      />

      <div className="grid gap-8 lg:grid-cols-[16rem_1fr]">
        <FilterPanel
          filters={filters}
          activeFilterCount={activeFilterCount}
          onToggleType={(type) => {
            update((next) => {
              const current = next.getAll('type');
              next.delete('type');
              for (const value of toggle(current, type)) next.append('type', value);
            });
          }}
          onTogglePricing={(model) => {
            update((next) => {
              const current = next.getAll('pricing');
              next.delete('pricing');
              for (const value of toggle(current, model)) next.append('pricing', value);
            });
          }}
          onCategory={(slug) => {
            update((next) => {
              if (slug === undefined) next.delete('category');
              else next.set('category', slug);
            });
          }}
          onToggleFlag={(name) => {
            update((next) => {
              if (next.get(name) === 'true') next.delete(name);
              else next.set(name, 'true');
            });
          }}
          onMinRating={(value) => {
            update((next) => {
              if (value === undefined) next.delete('minRating');
              else next.set('minRating', String(value));
            });
          }}
          onClear={() => {
            update((next) => {
              const query = next.get('q');
              for (const key of [...next.keys()]) next.delete(key);
              if (query !== null) next.set('q', query);
            });
          }}
        />

        <div>
          {firstPage.error !== undefined ? (
            <ErrorPanel error={firstPage.error} onRetry={firstPage.reload} />
          ) : firstPage.data === undefined ? (
            <SkeletonGrid count={9} />
          ) : applications.length === 0 ? (
            <EmptyState
              title="Nothing matched"
              description={
                activeFilterCount > 0
                  ? 'Try removing a filter, or search for something broader.'
                  : 'No applications have been published under this search yet.'
              }
            />
          ) : (
            <>
              <div className={classes(firstPage.isRefreshing && 'opacity-60 transition-opacity')}>
                <ApplicationGrid applications={applications} />
              </div>
              {nextCursor === undefined ? null : (
                <div className="mt-8 flex justify-center">
                  <Button
                    variant="secondary"
                    isLoading={isLoadingMore}
                    onClick={() => {
                      void loadMore();
                    }}
                  >
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

const PRICING_OPTIONS: readonly { value: PricingModel; label: string }[] = [
  { value: 'FREE', label: 'Free' },
  { value: 'ONE_TIME', label: 'One-time' },
  { value: 'SUBSCRIPTION', label: 'Subscription' },
  { value: 'USAGE_BASED', label: 'Usage-based' },
];

function FilterPanel({
  filters,
  activeFilterCount,
  onToggleType,
  onTogglePricing,
  onCategory,
  onToggleFlag,
  onMinRating,
  onClear,
}: {
  filters: CatalogFilters;
  activeFilterCount: number;
  onToggleType: (type: ApplicationType) => void;
  onTogglePricing: (model: PricingModel) => void;
  onCategory: (slug: string | undefined) => void;
  onToggleFlag: (name: 'verifiedOnly' | 'safeOnly') => void;
  onMinRating: (value: number | undefined) => void;
  onClear: () => void;
}): JSX.Element {
  const categories = useQuery((signal) => api.categories(signal), []);

  return (
    <aside className="card h-max lg:sticky lg:top-20">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-semibold text-slate-100">Filters</h2>
        {activeFilterCount > 0 ? (
          <button type="button" onClick={onClear} className="text-xs text-brand-soft hover:underline">
            Clear all
          </button>
        ) : null}
      </div>

      {categories.data === undefined || categories.data.items.length === 0 ? null : (
        <fieldset className="mb-5">
          <legend className="label">Category</legend>
          <Select
            aria-label="Category"
            value={filters.category ?? ''}
            options={[
              { value: '', label: 'All categories' },
              ...categories.data.items.map((category) => ({
                value: category.slug,
                label: category.name,
              })),
            ]}
            onChange={(value) => {
              onCategory(value === '' ? undefined : value);
            }}
          />
        </fieldset>
      )}

      <fieldset className="mb-5">
        <legend className="label">Application type</legend>
        <div className="space-y-0.5">
          {TYPE_OPTIONS.map((option) => (
            <SwitchRow
              key={option.value}
              label={option.label}
              checked={filters.type?.includes(option.value) ?? false}
              onChange={() => onToggleType(option.value)}
            />
          ))}
        </div>
      </fieldset>

      <fieldset className="mb-5">
        <legend className="label">Pricing</legend>
        <div className="space-y-0.5">
          {PRICING_OPTIONS.map((option) => (
            <SwitchRow
              key={option.value}
              label={option.label}
              checked={filters.pricing?.includes(option.value) ?? false}
              onChange={() => onTogglePricing(option.value)}
            />
          ))}
        </div>
      </fieldset>

      <fieldset className="mb-5 space-y-3">
        <legend className="label">Trust</legend>
        <Switch
          label="Verified developers only"
          checked={filters.verifiedOnly ?? false}
          onChange={() => onToggleFlag('verifiedOnly')}
        />
        <Switch
          label="Hide high-risk permissions"
          description="High risk means account-wide access or the ability to run arbitrary code."
          checked={filters.safeOnly ?? false}
          onChange={() => onToggleFlag('safeOnly')}
        />
      </fieldset>

      <fieldset>
        <legend className="label">Minimum rating</legend>
        <div className="flex gap-1.5">
          {[undefined, 3, 4].map((value) => (
            <button
              key={String(value)}
              type="button"
              onClick={() => onMinRating(value)}
              className={classes(
                'rounded-lg border px-2.5 py-1 text-xs',
                filters.minRating === value
                  ? 'border-brand/40 bg-brand/10 text-brand-soft'
                  : 'border-slate-700 text-slate-400 hover:text-slate-200',
              )}
            >
              {value === undefined ? 'Any' : `${String(value)}+ stars`}
            </button>
          ))}
        </div>
      </fieldset>
    </aside>
  );
}

function toggle(values: readonly string[], value: string): readonly string[] {
  return values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
}

function isPricingModel(value: string): value is PricingModel {
  return PRICING_OPTIONS.some((option) => option.value === value);
}

function isApplicationType(value: string): value is ApplicationType {
  return TYPE_OPTIONS.some((option) => option.value === value);
}

function readNumber(value: string | null): number | undefined {
  if (value === null) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** Relevance is meaningless without a search term, so an unsorted browse defaults to popularity. */
function readSort(value: string | null, query: string | null): CatalogSort {
  const matched = SORT_OPTIONS.find((option) => option.value === value);
  if (matched !== undefined) return matched.value;
  return query === null ? 'popular' : 'relevance';
}
